import parse from 'html-react-parser';

import { getElements, getConnections } from '../../api/blawksAPI'
import { ResultType } from '../../constants';

const kumuElements = require('../../api/kumu_elements.json');
const kumuEdges = require('../../api/kumu_edges.json');

export const getCategories = (kumuElements) => kumuElements.rows.filter(element => element.value.attributes["element type"] === "category")

export const getLabel = (element, language) => {
    return language == 'fr' ? getTranslatedText('fr',element.value.attributes.label) : getTranslatedText('en',element.value.attributes.label)
}

export const getActivityInfo = (element, language) => {
  const def = language === 'fr' 
    ? element.value.attributes['definition fr'] 
    : element.value.attributes.definition;

  return parse(def || "");
};

export const getTranslatedText = (language, string) => {
    if (!string.includes(" / ")) return string;

    const [english, french] = string.split(" / ");

    return language === "en" ? english : french;
};

export const getLocalizedText = (attributes, baseKey, language) => {
  if (!attributes) return "";
  if (language.startsWith("fr")) {
    return attributes[`${baseKey} fr`] || attributes[baseKey] || "";
  }
  return attributes[baseKey] || "";
};

export const getDescription = (element, language) => language !== 'fr' ? element.value.attributes.description : element.value.attributes['french description']

export const getResultType = (status) => status === 'required' ? ResultType.REQUIRED : ResultType.LEARN

export const getTag = (element) => element.value.attributes.tag[0]

export const getOptions = (options, language) => options.map(element => ({ id: element.id, label: removeTag(getLabel(element, language)), value: removeTag(getLabel(element, language)) }))

export const getOrder = (element) => element.value.attributes.order !== undefined ? element.value.attributes.order.split(",") : []

export const removeTag = (question) => (question.includes("Yes") || question.includes("No")) ? question.split(" ").slice(1).join(" ") : question

export const getDirectionsLabel = (element, language) => {
  if (!element?.value?.attributes) return "";

  const attrs = element.value.attributes;

  if (language === "fr") {
    return (
      attrs["map tool direction to user for this step fr"] || ""
    );
  }

  return attrs["map tool direction to user for this step"] || "";
};

export const orderBooleanOptions = (options) => {
    if (options.length === 2 && options.some(option => option.value === 'No') && options.some(option => option.value === 'Yes')) {
        const yesOption = options.find(option => option.value === 'Yes')
        const noOption = options.find(option => option.value === 'No')
        return [yesOption, noOption];
    }
    return options
}

export const formatHistory = (activity, question, answer, legislation, nextStep) => ({
    activity: activity,
    question: question,
    answer: answer,
    legislation: legislation,
    nextStep: nextStep
})

export const setResult = (results, name, status, reason, history, legislation, language) => {
    const resultValue = results.filter(result => result.name === name)
    let new_result = null
    if (resultValue.length === 1) {
        const removedResult = results.filter((result) => result.name !== name)
        new_result = removedResult.concat(
            {
                name: name,
                status: status,
                reason: reason == null ? resultValue[0].reason : reason,
                history: history,
                legislation: legislation
            }
        )
    } else {
        new_result = results.concat(
            {
                name: name,
                status: status,
                reason: reason,
                history: history,
                legislation: legislation 
            }
        )
    }
    return new_result
}

export const checkResultExists = (results, name) => results.filter(result => result.name === name).length === 1

export const getResultStatus = (results, name) => results.filter(result => result.name === name).length >= 1 ? results.filter(result => result.name === name)[0].status : null

export const getUniqueLegislation = (previousLegislation, newLegislation) => {
    const difference = newLegislation.filter(({ id: id1 }) => !previousLegislation.some(({ id: id2 }) => id2 === id1))
    return previousLegislation.concat(difference)
}

export const getLegislations = (activites, kumuEdges, kumuElements) => {
    const legislations = []
    activites.forEach(activity => {
        const id = activity.id
        const element_ids = kumuEdges.rows.filter(edge => edge.value.from_id === id).map(edge => edge.value.to_id)
        const linkedElements = kumuElements.rows.filter(element => element_ids.includes(element.id))
        linkedElements.forEach(legislation => {
            const legislation_id = legislation.id
            const exists = legislations.some(legislationPrev => legislationPrev.id === legislation_id)
            if (!exists) {
                legislations.push(legislation)
            }
        })
    })

    return legislations
}

export const getActivities = (kumuElements, kumuEdges, categories, language) => {
    const result = []
    categories.forEach(category => {
        const id = category.id
        const category_name = category.value.attributes.label
        const element_ids = kumuEdges.rows.filter(edge => edge.value.from_id === id).map(edge => edge.value.to_id)
        const linkedElements = kumuElements.rows.filter(element => element_ids.includes(element.id))
        const activties = []
        linkedElements.forEach(element => {
            const activity_name = getLabel(element, language)
            const info = getActivityInfo(element, language)
            activties.push({
                id: element.id,
                name: activity_name,
                info: info,
            })
        })
        result.push({
            name: category_name,
            activities: activties
        })
    })
    return result
}

export const removeLegislation = (legislation, toRemove) => legislation.filter(({ id: id1 }) => !toRemove.some(({ id: id2 }) => id2 === id1))

export const getLinkedLegislation = (kumuEdges, kumuElements, option) => {
    const elementIds = kumuEdges.rows.filter(edge => edge.value.from_id === option.id).map(edge => edge.value.to_id)
    const linkedElements = kumuElements.rows.filter(element => elementIds.includes(element.id))

    const legislations = linkedElements.filter(elm => elm.value.attributes["element type"] === "Legislation" || elm.value.attributes["element type"] === "Regulation")

    return legislations
}

export const getLinkedQuestions = (kumuEdges, kumuElements, id, order) => {
    const optionElementIds = kumuEdges.rows.filter(edge => edge.value.from_id === id).map(edge => edge.value.to_id)
    const linkedElements = kumuElements.rows.filter(element => optionElementIds.includes(element.id))

    const questions = linkedElements.filter(elm => elm.value.attributes["element type"] === "Question")

    return order.length === 0 ? questions : questions.sort((a, b) => order.indexOf(getTag(a)) - order.indexOf(getTag(b)));
}

export const getLinkedOptions = (kumuEdges, kumuElements, element) => {
    const optionElementIds = kumuEdges.rows.filter(edge => edge.value.from_id === element.id).map(edge => edge.value.to_id)

    return kumuElements.rows.filter(element => optionElementIds.includes(element.id))
}

export const getLinkedElements = (kumuEdges, kumuElements, question, history) => {
    const optionElementIds = kumuEdges.rows.filter(edge => edge.value.from_id === question.id).map(edge => edge.value.to_id)
    let linkedElements = kumuElements.rows.filter(element => optionElementIds.includes(element.id))
    // if(question !== undefined && history.some(element => element.question.id === question.id)){
    //     linkedElements = [kumuElements.rows.find(elm => elm.value.attributes["element type"] === "Next step")]
    // } 

    const statements = linkedElements.filter(elm => elm.value.attributes["element type"] === "statement" || elm.value.attributes["element type"] === "statement (multiple)")
    const reason = linkedElements.filter(elm => elm.value.attributes["element type"] === "reason")
    const result = linkedElements.filter(elm => elm.value.attributes["element type"] === "result")

    return {
        statements: statements,
        reason: reason,
        result: result
    }
}

export const getLinkedElementsFromMultiple = (kumuEdges, kumuElements, selectedOptions, history) => {
    // Collect all to_ids from all selected options
    const optionElementIds = kumuEdges.rows
      .filter(edge => selectedOptions.some(option => edge.value.from_id === option.id))
      .map(edge => edge.value.to_id);
  
    // Get all elements linked to selected options
    let linkedElements = kumuElements.rows.filter(element =>
      optionElementIds.includes(element.id)
    );
    
  
    const nextElementIds = kumuEdges.rows
    .filter(edge => linkedElements.some(elm => elm.id === edge.value.from_id))
    .map(edge => edge.value.to_id);

    // Step 3: find the elements corresponding to these edges
    const nextElements = kumuElements.rows.filter(elm => nextElementIds.includes(elm.id));

    // Step 4: filter only RESULT type
    const result = nextElements.filter(elm => elm.value.attributes["element type"]?.toLowerCase().trim() === "result");

    // Step 5: optionally enforce there is only one RESULT
    const finalResult = result.length > 0 ? [result[0]] : [];

    return {
    statements: linkedElements.filter(elm => elm.value.attributes["element type"]?.toLowerCase().includes("statement")),
    reason: linkedElements.filter(elm => elm.value.attributes["element type"]?.toLowerCase().trim() === "reason"),
    result: finalResult
    };
  };  


export const getNextLinkedElements = (kumuEdges, kumuElements, id, history) => {
    const optionElementIds = kumuEdges.rows.filter(edge => edge.value.from_id === id).map(edge => edge.value.to_id)
    let linkedElements = kumuElements.rows.filter(element => optionElementIds.includes(element.id))

    const question = linkedElements.find(elm => elm.value.attributes["element type"] === "Question")

    if (question !== undefined && history.some(element => element.question.id === question.id)) {
        linkedElements = [kumuElements.rows.find(elm => elm.value.attributes["element type"] === "Next step")]
    }

    const options = question !== undefined ? getLinkedOptions(kumuEdges, kumuElements, question) : []

    const legislations = linkedElements.filter(elm => elm.value.attributes["element type"] === "Legislation")
    const regulations = linkedElements.filter(elm => elm.value.attributes["element type"] === "Regulation")

    const nextStep = linkedElements.find(elm => elm.value.attributes["element type"] === "Next step") !== undefined ? true : false

    return {
        question: question,
        options: options,
        legislations: legislations.concat(regulations),
        regulations: regulations,
        nextStep: nextStep
    }
}

export const getResultDataForLegislation = (legislationLabel, language) => {
    // Clean the label to match
    const cleanLabel = (label) => {
        if (!label) return "";
        const doc = new DOMParser().parseFromString(label, 'text/html');
        const text = doc.body.textContent || label;
        return text.trim();
    };

    // Get the language-specific version of the label
    const cleanedLegislationLabel = cleanLabel(getTranslatedText(language, legislationLabel));
    
    // Find the result element that matches this legislation label
    const resultElement = kumuElements.rows.find(elem => {
        if (elem.value.attributes['element type'] === 'result') {
            const elemLabel = cleanLabel(getTranslatedText(language, elem.value.attributes.label));
            return elemLabel === cleanedLegislationLabel;
        }
        return false;
    });
    
    // If we found a result element, return it
    if (resultElement) {
        return resultElement;
    }
    
    // If no result element found, return null
    return null;
};
// -------------------- Sub-Tree Helpers --------------------

export const getLegislationST = (legislation, legislationIndex, setLegislationIndex) => {
    let legislationResult = null
    for (var i = legislationIndex; i < legislation.length; i++) {
        if (legislation[i].value.attributes.linkedtree !== undefined) {
            legislationResult = legislation[i]
            setLegislationIndex(i)
            break;
        }
    }
    return legislationResult;
}

export const getStartQuestionsST = (elements, edges) => {
    const startQuestionArr = elements.rows.filter(elm => elm.value.attributes["element type"] === "Start Question")
    const startQuestion = startQuestionArr.length === 1 ? startQuestionArr[0] : []

    return getLinkedElementsST(startQuestion, elements, edges)
}

export const getElementsST = async (url) => {
    const kumuURL = url.split('/')
    const username = kumuURL[3]
    const treeName = kumuURL[4]
    let elements = kumuElements
    // getElements(username, treeName).then(res => {
    //     elements = res
    // })
    return elements
}

// export const getEdgesST = (legislation) =>  {
//     const edges = legislation.value.attributes.edges_link !== undefined ? require('../../api/' + legislation.value.attributes.edges_link) : null
//     return edges
// }

export const getEdgesST = async (url) => {
    const kumuURL = url.split('/')
    const username = kumuURL[3]
    const treeName = kumuURL[4]
    let edges = kumuEdges
    // getConnections(username, treeName).then(res => {
    //     edges = res
    // })
    return edges
}


export const getLinkedOptionsST = (element, elements, edges) => {
    const optionElementIds = edges.rows.filter(edge => edge.value.from_id === element.id).map(edge => edge.value.to_id)
    return elements.rows.filter(element => optionElementIds.includes(element.id))
}

export const getLinkedElementsST = (question, elements, edges) => {
    const options = getLinkedOptionsST(question, elements, edges)

    return {
        question: question,
        options: options,
    }
}

export const getNextLegislationST = (legislation, index, setLegislationIndex) => {
    const nextIndex = index + 1

    if (nextIndex >= legislation.length) return 0
    return getLegislationST(legislation, nextIndex, setLegislationIndex)
}

export const getNextLinkedElementsST = (id, elements, edges) => {
    const optionElementIds = edges.rows.filter(edge => edge.value.from_id === id).map(edge => edge.value.to_id)
    let linkedElements = elements.rows.filter(element => optionElementIds.includes(element.id))

    const question = linkedElements.find(elm => elm.value.attributes["element type"] === "Question")
    const result = linkedElements.find(elm => elm.value.attributes["element type"] === "Result")

    const options = question !== undefined ? getLinkedOptionsST(question, elements, edges) : []

    return {
        question: question,
        options: options,
        result: result
    }
}

export const getLegislationResult = (legislation, currentElement, questionHistory) => {
    return {
        title: legislation.value.attributes.label,
        reason: currentElement.result.value.attributes.label,
        result: currentElement.result,
        isRequired: legislation.value.attributes.label === 'Species at Risk Act' || legislation.value.attributes.label === 'Canadian Navigable Waters Act' ? false : true,
        questionsAnswered: questionHistory
        // ["You are working on a Rare Earth mineral mine.", "You are opening a mine.", "The production capacity is estimated to be 2500 t/d or more."]
    }
}